import { hasLength } from 'utility/functions';
import { MULTI_SELECT_FIELDS } from '../const';
import { PartialVLF } from '../types';

/**
 * Converts staged filters to URL query parameters, removing empty values.
 * Handles strings, arrays, and removes if no length.
 *
 * @param prevQueryParams - Existing URLSearchParams to build upon
 * @param stagedFilters - Filter object to convert to query parameters
 * @param repeatQpKeys - key list for repeat format instead of default comma join
 * @returns New URLSearchParams with updated filter values
 *
 * @example
 * const params = new URLSearchParams('?sort=date');
 * const filters = { type: ['article', 'video'], author: 'john' };
 * const result = cleanStagedFiltersToQueryParams(params, filters);
 * // Result: ?sort=date&type=article,video&author=john
 */
export function cleanStagedFiltersToQueryParams(
  prevQueryParams: URLSearchParams,
  stagedFilters: PartialVLF,
  repeatQpKeys: string[] = [],
): URLSearchParams {
  const newQueryParams = new URLSearchParams(prevQueryParams.toString());

  Object.entries(stagedFilters).forEach(([key, value]) => {
    // Clear existing values for this key
    newQueryParams.delete(key);

    // No value - key already deleted
    if (!hasLength(value)) {
      return;
    }

    // Handle single / string values
    if (typeof value === 'string') {
      newQueryParams.set(key, value);
      return;
    }

    // Handle array values - repeat vs comma format
    if (Array.isArray(value)) {
      if (repeatQpKeys.includes(key)) {
        // Repeat query param format (edge cases)
        value.forEach((item) => {
          if (item) {
            newQueryParams.append(key, item);
          }
        });
      } else {
        // Default (common) - comma joined under one qp key
        newQueryParams.set(key, value.join(','));
      }
      return;
    }

    // No case matched, log warning.
    console.warn('[cleanStagedFiltersToQueryParams] unhandled kv:', key, value);
  });

  return newQueryParams;
}

/**
 * Normalizes filter values to ensure proper data types for multi-select vs single-select fields
 * Multi-select fields are always arrays, single-select fields are always strings
 */
export function normalizeFiltersForDataType(
  filters: Partial<Record<string, any>>,
): PartialVLF {
  const normalized: PartialVLF = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (MULTI_SELECT_FIELDS.includes(key)) {
        // Ensure multi-select fields are arrays
        if (Array.isArray(value)) {
          normalized[key] = value.filter(
            (v) => v !== undefined && v !== null && v !== '',
          );
        } else if (typeof value === 'string') {
          normalized[key] = value.includes(',')
            ? value.split(',').filter((v) => v.trim() !== '')
            : [value];
        } else {
          normalized[key] = [String(value)];
        }
      } else {
        // Ensure single-select fields are strings
        normalized[key] = Array.isArray(value) ? value.join(',') : String(value);
      }
    } else if (MULTI_SELECT_FIELDS.includes(key)) {
      // For multi-select fields that are empty/null/undefined, set to empty array
      normalized[key] = [];
    }
  });

  return normalized;
}

/**
 * Checks if a field is a multi-select field
 */
export function isMultiSelectField(fieldKey: string): boolean {
  return MULTI_SELECT_FIELDS.includes(fieldKey);
}

/**
 * Safely updates a filter value with proper type handling
 */
export function normalizeFilterValue(
  key: string,
  value: string | string[],
): string | string[] {
  // Handle empty values
  if (value === undefined || value === null || value === '') {
    return isMultiSelectField(key) ? [] : '';
  }

  // Ensure proper data type based on field type
  if (isMultiSelectField(key)) {
    // Multi-select fields should always be arrays
    if (Array.isArray(value)) {
      return value.filter((v) => v !== undefined && v !== null && v !== '');
    } else if (typeof value === 'string') {
      return value.includes(',')
        ? value.split(',').filter((v) => v.trim() !== '')
        : [value];
    } else {
      return [String(value)];
    }
  } else {
    // Single-select fields should be strings
    return Array.isArray(value) ? value.join(',') : String(value);
  }
}

/**
 * Calculates the number of applied filters, excluding specified fields
 */
export function calculateAppliedFiltersCount(
  queryState: Record<string, any>,
  excludeFilters: string[] = [],
): number {
  return Object.entries(queryState).reduce((count, [key, value]) => {
    if (excludeFilters.includes(key)) {
      return count; // Skip excluded fields
    }
    if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      (!Array.isArray(value) || value.length > 0)
    ) {
      return count + 1;
    }
    return count;
  }, 0);
}
